/* eslint-disable no-shadow */
import React from 'react';

import { Grid, Tooltip } from '@material-ui/core';
import { StyledAvatar } from 'components/avatars';
import { compareDesc, format } from 'date-fns';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useReview } from 'screens/performanceReview/ReviewContext';
import styled from 'styled-components';
import { StyledTypography, StyledButton } from 'styles/common';

import {
  REVIEW_RESPONSE_PROVIDER_TYPE_TITLE_MAP,
  REVIEW_SUBMISSION_STATUS_MAP,
  FEEDBACK_FORM_TYPE,
  CONTINUOUS_FEEDBACK_FORM_STATUS,
} from 'constants/ProjectConstants';
import { getReviewFormRoute, pushFeedbackParams, downloadOrOpenFile } from 'constants/helper';
import { sendOpenedNominationPanelEvent, sendRespondedToFeedbackRequest } from 'constants/mixpanelHelper';
import { sendOpenedReviewFormEvent } from 'constants/mixpanelHelpers/reviewsEvents';
import theme from 'constants/theme';

import { acceptFeedback, declineFeedback } from 'services/continuousFeedback';
import { showNominatePeers } from 'services/nominatePeers';
import { acceptNomination, rejectNomination } from 'services/performanceReview';
import { showErrorSnackbar } from 'services/snackbar';

import DateComp from 'uiToolkit/DateComp';

const FEEDBACK_REQUESTED = 'Feedback Requested';
const SHARE_FEEDBACK = 'Share Feedback';

const ReviewRowContainer = styled(Grid)`
  padding: 1rem 0rem 1rem 0rem;
  border-bottom: 1px solid #eaeaea;
  margin: 0 1.5rem 0 1.5rem;
  width: calc(100% - 3rem);
`;

const ButtonContainer = styled.div`
  ${({ left }) =>
    left &&
    `
    text-align: left;
  `};
  ${({ right }) =>
    right &&
    `
    text-align: right;
  `};
  text-align: center;
  button {
    width: 92.5%;
  }
`;

const ReviewRowDisplay = ({
  employee,
  review,
  endDateFormatted,
  startDate,
  updatePeerStatus,
  showDateColumn,
  resetData,
  loggedInEmployee,
  isCompletedReviewRow = false,
}) => {
  const history = useHistory();
  const { setSelectedReviewDetails } = useReview();
  const dispatch = useDispatch();

  const goToFormReviewRoute = (reviewId, employeeId) => {
    sendOpenedReviewFormEvent({
      eventProperties: {
        reviewCycleId: reviewId,
        empId: employeeId,
        source: isCompletedReviewRow ? 'Completed Reviews' : 'Pending Reviews',
        providerType: review.providerType,
      },
    });
    setSelectedReviewDetails({
      reviewId,
      employeeId,
      submissionStatus: review.submissionStatus,
      reviewProviderType: review.providerType,
      reviewStage: review.feedbackForm.currentStage,
      isPublished: review.isPublished,
    });
    history.push(getReviewFormRoute(reviewId, employeeId));
  };

  const handleAcceptClick = (review) => {
    const acceptTrigger = review.providerType === 'PEER' ? acceptNomination : acceptFeedback;
    const requestDetails = () => {
      if (review.providerType === 'PEER') {
        return {
          reviewId: review.feedbackForm.id,
          employeeId: review.receiverEmployeeId,
        };
      }
      return {
        feedbackId: review.id,
      };
    };

    acceptTrigger(requestDetails())
      .then(() => {
        if (review.providerType !== 'PEER') {
          sendRespondedToFeedbackRequest({
            eventProperties: {
              response: 'Accept',
            },
          });
        }
        if (review.responseType === 'CONTINUOUS_FEEDBACK') {
          updatePeerStatus(review.id, review.receiverEmployeeId, review.responseType);
        } else {
          updatePeerStatus(review.feedbackForm.id, review.receiverEmployeeId, review.providerType);
        }
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message || 'Some error occured'));
      });
  };

  const handleRejectClick = (review) => {
    const rejectTrigger = review.providerType === 'PEER' ? rejectNomination : declineFeedback;

    const requestDetails = () => {
      if (review.providerType === 'PEER') {
        return {
          reviewId: review.feedbackForm.id,
          employeeId: review.receiverEmployeeId,
        };
      }
      return {
        feedbackId: review.id,
      };
    };

    rejectTrigger(requestDetails())
      .then(() => {
        if (review.providerType !== 'PEER') {
          sendRespondedToFeedbackRequest({
            eventProperties: {
              response: 'Reject',
            },
          });
        }
        // Resetting data of table is the only option as pagination breaks when we reject any nomination.
        resetData();
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message || 'Some error occured'));
      });
  };

  const handleNominateClick = (review) => {
    sendOpenedNominationPanelEvent({
      eventProperties: {
        reviewCycleId: review.id,
        source: 'Pending Reviews',
        empId: loggedInEmployee.id,
      },
    });
    dispatch(showNominatePeers({ reviewId: review.feedbackForm.id }));
  };

  const handleSubmissionStatusClick = (review) => {
    if (review.summaryFilePath) {
      downloadOrOpenFile(review.summaryFilePath);
    } else if (review.responseType === 'CONTINUOUS_FEEDBACK') {
      const feedbackFormType = () => {
        if (review.feedbackType === FEEDBACK_FORM_TYPE.PROVIDED) return FEEDBACK_FORM_TYPE.SHOW_PROVIDED;
        if (
          review.feedbackType === FEEDBACK_FORM_TYPE.REQUESTED &&
          review.submissionStatus === CONTINUOUS_FEEDBACK_FORM_STATUS.COMPLETED
        )
          return FEEDBACK_FORM_TYPE.SHOW_REQUESTED;
        if (
          review.feedbackType === FEEDBACK_FORM_TYPE.REQUESTED &&
          review.submissionStatus === CONTINUOUS_FEEDBACK_FORM_STATUS.NOT_STARTED
        )
          return FEEDBACK_FORM_TYPE.PROVIDING_REQUESTED;
        return null;
      };
      const feedbackFormTypeMapped = feedbackFormType();
      pushFeedbackParams({
        history,
        formType: feedbackFormTypeMapped,
        typeMapped: true,
        feedbackId: review.id,
      });
    } else {
      goToFormReviewRoute(review.feedbackForm.id, review.receiverEmployeeId);
    }
  };

  const isNominated = (review) =>
    (review.providerType === 'PEER' && review.peerStatus === 'NOMINATED') ||
    (review.responseType === 'CONTINUOUS_FEEDBACK' &&
      review.peerStatus === 'NOMINATED' &&
      review.submissionStatus !== 'SUBMITTED');

  const daysFromDeadline = compareDesc(new Date(), new Date(startDate));
  const isStartBtnDisabled = review.submissionStatus === 'NOT_STARTED' && daysFromDeadline > 0;

  return (
    <ReviewRowContainer container>
      <Grid container item xs={3} alignItems="center">
        <StyledAvatar src={employee.profilePhotoSrc} size="smaller" variant="circular" rightGutter={2} />
        <StyledTypography leftGutter={1} variant="body1">
          {employee.displayName}
        </StyledTypography>
      </Grid>
      <Grid container item xs={2} alignItems="center">
        <StyledTypography customColor={theme.palette.grey.main}>
          {review.responseType === 'CONTINUOUS_FEEDBACK' &&
            (loggedInEmployee?.id.toString() === review.receiverEmployeeId.toString()
              ? FEEDBACK_REQUESTED
              : SHARE_FEEDBACK)}
          {review.responseType !== 'CONTINUOUS_FEEDBACK' &&
            REVIEW_RESPONSE_PROVIDER_TYPE_TITLE_MAP[review.providerType]}
        </StyledTypography>
      </Grid>
      <Grid container item xs={3} alignItems="center">
        <StyledTypography customColor={theme.palette.grey.main}>
          {review.responseType === 'CONTINUOUS_FEEDBACK'
            ? 'Continuous Feedback'
            : review.feedbackForm && review.feedbackForm.reviewCycle && review.feedbackForm.reviewCycle.name}
        </StyledTypography>
      </Grid>
      <Grid container item xs={2} alignItems="center">
        {showDateColumn && endDateFormatted && (
          <DateComp
            tooltip="Due by"
            editable={false}
            startDateTime={endDateFormatted.timeStamp}
            color={endDateFormatted.color}
            isCrossed={endDateFormatted.isCrossed}
          />
        )}
      </Grid>
      <Grid container item xs={2} alignItems="center">
        {review.providerType === 'NOMINATOR' && (
          <StyledButton
            size="large"
            fullWidth
            variant="outlined"
            color="primary"
            onClick={() => handleNominateClick(review)}
          >
            <StyledTypography variant="body1" color="primary" clickable>
              Nominate
            </StyledTypography>
          </StyledButton>
        )}
        {review.providerType !== 'NOMINATOR' &&
          (isNominated(review) ? (
            <Grid container spacing={10}>
              <Grid item xs={6}>
                <ButtonContainer left>
                  <StyledButton
                    size="large"
                    fullWidth
                    variant="outlined"
                    customColor="primary"
                    borderColor="primary"
                    onClick={() => handleAcceptClick(review)}
                  >
                    <StyledTypography variant="body1" color="#FFF" clickable>
                      Accept
                    </StyledTypography>
                  </StyledButton>
                </ButtonContainer>
              </Grid>
              <Grid item xs={6}>
                <ButtonContainer right>
                  <StyledButton
                    size="large"
                    fullWidth
                    variant="outlined"
                    color="primary"
                    onClick={() => handleRejectClick(review)}
                  >
                    <StyledTypography variant="body1" color="primary" clickable>
                      Reject
                    </StyledTypography>
                  </StyledButton>
                </ButtonContainer>
              </Grid>
            </Grid>
          ) : (
            <Tooltip
              title={startDate ? `You can start this from ${format(new Date(startDate), 'dd/MM/yyyy')}` : ''}
              disableHoverListener={!isStartBtnDisabled}
              disableFocusListener={!isStartBtnDisabled}
            >
              <span
                style={{
                  cursor: isStartBtnDisabled ? 'not-allowed' : '',
                }}
              >
                <StyledButton
                  size="large"
                  fullWidth
                  variant="outlined"
                  color="primary"
                  disabled={isStartBtnDisabled}
                  onClick={() => handleSubmissionStatusClick(review)}
                >
                  <StyledTypography variant="body1" color="primary" clickable>
                    {REVIEW_SUBMISSION_STATUS_MAP[review.submissionStatus].actionText}
                  </StyledTypography>
                </StyledButton>
              </span>
            </Tooltip>
          ))}
      </Grid>
    </ReviewRowContainer>
  );
};

export default ReviewRowDisplay;
